"use client";

import { c as _c } from "react/compiler-runtime";
import { Checkbox } from "@/components/ui/Checkbox";
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/Command";
import { Spinner } from "@/components/ui/Spinner";
import { Check } from "lucide-react";
import * as React from "react";
import cn from "twsx";
import type { Option } from "./types";
type ComboboxOptionListProps = {
  commandListStyle: React.CSSProperties;
  filteredOptions: Option[];
  groupedOptions: Record<string, Option[]> | null;
  isLoading: boolean;
  maxLength: number;
  multi: boolean;
  selectedValue: string | string[] | undefined;
  showSelectionCheck: boolean;
  onCheckboxClick: (event: React.MouseEvent, optionValue: string) => void;
  onSelect: (optionValue: string) => void;
};
function ComboboxOptionItem(t0) {
  const $ = _c(39);
  const {
    multi,
    maxLength,
    option,
    selectedValue,
    showSelectionCheck,
    onCheckboxClick,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== option.value || $[1] !== selectedValue) {
    t1 = Array.isArray(selectedValue) && selectedValue.includes(option.value);
    $[0] = option.value;
    $[1] = selectedValue;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isMultiSelected = t1;
  let t2;
  if ($[3] !== maxLength || $[4] !== option.value || $[5] !== selectedValue) {
    t2 = Array.isArray(selectedValue) && selectedValue.length >= maxLength && !selectedValue.includes(option.value);
    $[3] = maxLength;
    $[4] = option.value;
    $[5] = selectedValue;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const isAtLimit = t2;
  const isSelected = selectedValue === option.value;
  const t3 = option.value;
  const t4 = option.disabled || isAtLimit;
  let t5;
  if ($[7] !== onSelect || $[8] !== option.value) {
    t5 = () => onSelect(option.value);
    $[7] = onSelect;
    $[8] = option.value;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = option.subtitle && "h-auto min-h-11 py-1.5";
  let t7;
  if ($[10] !== t6) {
    t7 = cn("gap-2.5 [@media(pointer:coarse)]:min-h-11", t6);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== isAtLimit || $[13] !== isMultiSelected || $[14] !== multi || $[15] !== onCheckboxClick || $[16] !== option.disabled || $[17] !== option.value) {
    t8 = multi && <Checkbox checked={isMultiSelected} disabled={option.disabled || isAtLimit} onClick={event => onCheckboxClick(event, option.value)} className="shrink-0" />;
    $[12] = isAtLimit;
    $[13] = isMultiSelected;
    $[14] = multi;
    $[15] = onCheckboxClick;
    $[16] = option.disabled;
    $[17] = option.value;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== option.label) {
    t9 = <span className="truncate">{option.label}</span>;
    $[19] = option.label;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== option.subtitle) {
    t10 = option.subtitle && <span className="truncate text-xs text-text-tertiary">{option.subtitle}</span>;
    $[21] = option.subtitle;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t9) {
    t11 = <div className="flex min-w-0 grow flex-col gap-0">{t9}{t10}</div>;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== isSelected || $[27] !== showSelectionCheck) {
    t12 = showSelectionCheck && <Check className={cn("ml-auto !size-3.5 !text-text-primary", isSelected ? "opacity-100" : "opacity-0")} />;
    $[26] = isSelected;
    $[27] = showSelectionCheck;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== option.badge || $[30] !== option.icon || $[31] !== option.value || $[32] !== t11 || $[33] !== t12 || $[34] !== t4 || $[35] !== t5 || $[36] !== t7 || $[37] !== t8) {
    t13 = <CommandItem value={t3} disabled={t4} onSelect={t5} className={t7}>{t8}{option.icon}{t11}{option.badge}{t12}</CommandItem>;
    $[29] = option.badge;
    $[30] = option.icon;
    $[31] = option.value;
    $[32] = t11;
    $[33] = t12;
    $[34] = t4;
    $[35] = t5;
    $[36] = t7;
    $[37] = t8;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}
export function ComboboxOptionList(t0) {
  const $ = _c(13);
  const {
    commandListStyle,
    filteredOptions,
    groupedOptions,
    isLoading,
    maxLength,
    multi,
    selectedValue,
    showSelectionCheck,
    onCheckboxClick,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== filteredOptions || $[1] !== groupedOptions || $[2] !== isLoading || $[3] !== maxLength || $[4] !== multi || $[5] !== onCheckboxClick || $[6] !== onSelect || $[7] !== selectedValue || $[8] !== showSelectionCheck) {
    t1 = isLoading ? <div className="flex items-center justify-center py-6"><Spinner size="md" tone="muted" /></div> : <><CommandEmpty>No option found.</CommandEmpty>{groupedOptions ? Object.entries(groupedOptions).map(t2 => {
        const [group, groupOptions] = t2;
        return <CommandGroup key={group} heading={group}>{groupOptions.map(option => <ComboboxOptionItem key={option.value} multi={multi} maxLength={maxLength} option={option} selectedValue={selectedValue} showSelectionCheck={showSelectionCheck} onCheckboxClick={onCheckboxClick} onSelect={onSelect} />)}</CommandGroup>;
      }) : <CommandGroup>{filteredOptions.map(option_0 => <ComboboxOptionItem key={option_0.value} multi={multi} maxLength={maxLength} option={option_0} selectedValue={selectedValue} showSelectionCheck={showSelectionCheck} onCheckboxClick={onCheckboxClick} onSelect={onSelect} />)}</CommandGroup>}</>;
    $[0] = filteredOptions;
    $[1] = groupedOptions;
    $[2] = isLoading;
    $[3] = maxLength;
    $[4] = multi;
    $[5] = onCheckboxClick;
    $[6] = onSelect;
    $[7] = selectedValue;
    $[8] = showSelectionCheck;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== commandListStyle || $[11] !== t1) {
    t2 = <CommandList className="min-h-0 overscroll-contain" style={commandListStyle}>{t1}</CommandList>;
    $[10] = commandListStyle;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}